import { createTheme, getContrastRatio } from '@mui/material/styles';
import { AppMode, THEME_PALETTE_COLORS, ThemePaletteName } from '../contexts/UserPreferencesContext';
import { blendHexColors, darkAppearanceVariables, nightAppearanceVariables } from './appearance';

// Create custom theme for Nadi astrology app
export const createAppTheme = (mode: AppMode, themePalette: ThemePaletteName = 'green') => {
  const palette = THEME_PALETTE_COLORS[themePalette] || THEME_PALETTE_COLORS.green;
  const muiMode = mode === 'light' ? 'light' : 'dark';
  const isDark = mode !== 'light';
  const isNight = mode === 'night';
  const brand = isDark && getContrastRatio(palette.main, '#ffffff') < 4.5 ? palette.dark : palette.main;
  const brandPressed = palette.dark || '#0A5240';
  const brandSoft = isDark ? blendHexColors(brand, isNight ? '#0D1A11' : '#0F1F16', 0.18) : themePalette === 'green' ? '#E1F5EE' : blendHexColors(brand, '#FFFFFF', 20 / 255);
  const brandOnSoft = isDark ? palette.light : themePalette === 'green' ? '#085041' : brandPressed;
  const neutrals = {
    page: isNight ? '#050805' : isDark ? '#07130D' : '#F6F7F6',
    card: isNight ? '#0D1A11' : isDark ? '#0F1F16' : '#FFFFFF',
    border: isNight ? '#203327' : isDark ? '#26352C' : '#E6E8E6',
    borderStrong: isNight ? '#31503B' : isDark ? '#3A4A40' : '#D2D6D3',
    text: isNight ? '#F2FFF4' : isDark ? '#E8EEE9' : '#1A1D1B',
    secondary: isNight ? '#BCD3C1' : isDark ? '#A8B7AD' : '#5B635E',
    tertiary: isNight ? '#89A38F' : isDark ? '#748079' : '#9AA09B',
    danger: isDark ? '#F87171' : '#C0392B',
  };

  return createTheme({
  palette: {
    mode: muiMode,
    primary: {
      main: brand,
      light: brandSoft,
      dark: brandPressed,
      contrastText: '#ffffff'
    },
    secondary: {
      main: brandOnSoft,
      light: brandSoft,
      dark: brandPressed,
      contrastText: isDark ? '#07130D' : '#ffffff'
    },
    background: {
      default: neutrals.page,
      paper: neutrals.card
    },
    text: {
      primary: neutrals.text,
      secondary: neutrals.secondary
    },
    divider: neutrals.border,
    error: {
      main: neutrals.danger,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Open Sans", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      color: neutrals.text
    },
    h2: {
      fontWeight: 600,
      color: neutrals.text
    },
    h3: {
      fontWeight: 600,
      color: neutrals.text
    },
    h4: { fontWeight: 600, color: neutrals.text },
    h5: { fontWeight: 600, color: neutrals.text },
    h6: { fontWeight: 600, color: neutrals.text },
    body1: { lineHeight: 1.6, fontWeight: 400 },
    body2: { fontWeight: 400 },
  },
  components: {
    MuiCssBaseline: {
        styleOverrides: {
          body: {
            colorScheme: muiMode,
            '--app-ink-primary': isDark ? palette.light : palette.main,
            '--app-ink-secondary': brandOnSoft,
            '--app-ink-success': isDark ? '#99dfb0' : '#1b5e20',
            '--app-ink-warning': isDark ? '#ffd08a' : '#e65100',
            '--app-ink-error': isDark ? '#ffb4b4' : '#c62828',
            '--app-ink-info': isDark ? '#94ceff' : '#01579b',
            '--app-surface-primary': brandSoft,
            '--app-surface-secondary': brandSoft,
            '--app-surface-success': isNight ? '#10281b' : isDark ? '#163627' : '#4caf50',
            '--app-surface-warning': isNight ? '#2a2013' : isDark ? '#382c1c' : '#ffb74d',
            '--app-surface-error': isNight ? '#2c191c' : isDark ? '#3b2326' : '#ef5350',
            '--app-surface-info': isNight ? '#102233' : isDark ? '#172e43' : '#03a9f4',
            ...(isDark ? (isNight ? nightAppearanceVariables : darkAppearanceVariables) : {}),
            ...(isDark ? {
              '--aat-bg': neutrals.page, '--aat-card': neutrals.card, '--aat-ink': neutrals.text,
              '--aat-muted': neutrals.secondary, '--aat-border': '#516258', '--aat-border-strong': '#83968a',
              '--aat-accent': '#2e7d52', '--aat-accent-strong': '#1f513c',
              '--aat-accent-soft': isNight ? '#10281b' : '#163627', '--aat-gold': '#8a6500', '--aat-danger': '#ffb4b4',
            } : {}),
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ ownerState }) => isDark && ownerState.color === 'primary'
            ? { color: palette.light } : {},
        },
      },
      MuiSvgIcon: { styleOverrides: { colorPrimary: isDark ? { color: palette.light } : {}, colorSecondary: isDark ? { color: palette.light } : {} } },
      MuiTab: { styleOverrides: { root: isDark ? { '&.Mui-selected': { color: palette.light } } : {} } },
      MuiLink: { styleOverrides: { root: isDark ? { color: palette.light } : {} } },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          minHeight: 44,
          padding: '10px 20px',
          fontWeight: 600,
          boxShadow: 'none',
          ...(isDark ? { '&.MuiButton-textPrimary': { color: palette.light } } : {}),
        },
        containedPrimary: {
          backgroundColor: brand,
          '&:hover': {
            backgroundColor: brandPressed,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: neutrals.borderStrong,
          color: neutrals.text,
        },
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          border: `1px solid ${neutrals.border}`,
          backgroundImage: 'none',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: neutrals.border,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: neutrals.card,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutrals.borderStrong,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: neutrals.borderStrong,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand,
            borderWidth: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: neutrals.secondary,
          fontWeight: 500,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: neutrals.borderStrong,
          color: neutrals.secondary,
          fontWeight: 600,
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: brandSoft,
            color: brandOnSoft,
            '&:hover': {
              backgroundColor: brandSoft,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: brandSoft,
          color: brandOnSoft,
        },
      },
    }
  }
  });
};

